import type { NewsThreadMetadata } from "@moderation-contract/news-thread-core";
import { stripAllPua } from "@nijiurachan/pua-command";
import type { Attachment } from "@/entities/attachment";

type PollOption = {
  id: string;
  label: string;
};

export type PollDefinition = {
  closesAt: string;
  options: PollOption[];
};

type PollResults = {
  totalVotes: number;
  options: Array<{
    optionId: string;
    voteCount: number;
  }>;
};

export type PollState = {
  isClosed: boolean;
  selectedOptionId: string | null;
  results: PollResults | null;
};

export type PollCreateInput = {
  durationMinutes: number;
  options: string[];
};

/** 表示用に分解した本文の1行 */
export interface PostBodyLine {
  type: "text" | "quote";
  text: string;
}

/** backend PostView */
export interface Post {
  id: string;
  threadId: string;
  seq: number;
  /** 投稿ごとの通し番号 (旧AI_BBSのNo.)。backfill前の移行行のみ null */
  postSequenceNo: number | null;
  status: "public" | "shadowed" | "trash" | "unavailable";
  body: string;
  createdAt: string;
  /**
   * Ordered attachments returned by the current API.
   * Response normalization always materializes this array.
   */
  attachments?: Attachment[];
  /** Legacy API field retained at the response boundary and for old snapshots. */
  attachment?: Attachment | null;
  sodaneCount: number;
  /** アーカイブ時点のdel数。旧レスポンス互換のため未提供時はundefined。 */
  delCount?: number;
  displayId: string | null;
  /** 表示IDの由来。旧APIレスポンスでは省略されることがある。 */
  displayIdSource: "moderation" | "thread" | null;
  /** このレスがsage指定で投稿されたか (スレッドを浮上させない指定)。OPは常にfalse */
  sage: boolean;
  /** スレ立て本人が明示的に「スレ主」表示を付けた返信。 */
  showThreadOwner?: boolean;
  /** 公開chunkとfull threadに含まれる不変のpoll定義。 */
  poll?: PollDefinition | null;
  /** ThreadStateから合成する閲覧者別・可変のpoll状態。 */
  pollState?: PollState | null;
  /** 記事画像を添付とは独立して表示するニュース投稿metadata。 */
  news?: NewsThreadMetadata;
}

export type PostAttachmentFields = Pick<Post, "attachment" | "attachments">;

/**
 * Return ordered attachments while accepting legacy singular responses.
 * An explicitly supplied array is authoritative, including an empty array.
 */
export function getPostAttachments(post: PostAttachmentFields): Attachment[] {
  if (Array.isArray(post.attachments)) {
    return post.attachments;
  }
  return post.attachment ? [post.attachment] : [];
}

/**
 * Normalize the API boundary without removing the singular compatibility view
 * used by existing catalog and NG-filter paths.
 */
export function normalizePostAttachments<T extends PostAttachmentFields>(
  post: T,
): T & { attachments: Attachment[]; attachment: Attachment | null } {
  const attachments = getPostAttachments(post);
  return {
    ...post,
    attachments,
    attachment: attachments[0] ?? null,
  };
}

/** 表示・引用に使う「No.」の値。通し番号が無い行はスレ内連番へフォールバックする。 */
export function postNo(post: Pick<Post, "seq" | "postSequenceNo">): number {
  return post.postSequenceNo ?? post.seq;
}

export function getPostBodyLines(body: string): PostBodyLine[] {
  return body.split(/\r?\n/).map((text) => {
    const quotePrefix = text.trimStart();
    return {
      type:
        quotePrefix.startsWith(">") || quotePrefix.startsWith("&gt;")
          ? "quote"
          : "text",
      text,
    };
  });
}

/**
 * 装飾マーカー (PUA) を除いたプレーン本文。
 * コピー・読み上げ・NG照合・引用元解決・プレビュー等、装飾抜きの生テキストが
 * 必要な箇所は post.body を直接使わずこれを経由する。
 */
export function getPostPlainBody(post: Pick<Post, "body">): string {
  return stripAllPua(post.body);
}
